<template>
  <b-container fluid>
    <b-row>
      <b-col md="8">
        <iq-card>
          <template v-slot:headerTitle>
            <h3 class="card-title">
              Your Schedule
              <Popper :hover="true" placement="right">
                <button class="btn info-btn">
                  <i class="ion-help-circled"></i>
                </button>
                <template #content
                  ><div class="popper-content">
                    Blue = Your appointment <br />
                    Green = Your appointment with the selected patient
                    <br />
                    Black = Appointment for the selected patient with a
                    different doctor
                  </div></template
                >
              </Popper>
            </h3>
          </template>

          <template v-slot:default>
            <FullCalendar
              ref="fullCalendar"
              id="calendar"
              :options="calendarOptions"
            />
          </template>
        </iq-card>
      </b-col>
      <b-col md="4">
        <iq-card>
          <template v-slot:headerTitle>
            <h4 class="card-title">Book Appointment</h4>
          </template>
          <template v-slot:default>
            <Form
              @submit="bookAppointment"
              class="form-group"
              ref="form"
              :validation-schema="schema"
            >
              <div class="form-group">
                <label> Pick the patient: </label>
                <select
                  name="patientSelect"
                  id="patientSelect"
                  class="form-control"
                  v-model="selectedPatient"
                  @change="selectedPatientChanged"
                >
                  <option value="default" selected>Select a patient</option>
                  <option v-for="user in users" :value="user.id" :key="user.id">
                    {{
                      user.email + ", " + user.firstName + " " + user.lastName
                    }}
                  </option>
                </select>
                <p>{{ selectVerification }}</p>
              </div>
              <div class="form-group" v-if="!showLocation">
                <label>
                  Check box if this will be an online appointment:
                </label>
                <input
                  name="zoomCheckbox"
                  id="zoomCheckbox"
                  type="checkbox"
                  @change="showZoom = !showZoom"
                />
              </div>
              <div v-if="showZoom" class="form-group">
                <label> Provide video call link: </label>
                <Field
                  name="zoomLink"
                  id="zoomLink"
                  class="form-control"
                  type="text"
                />
              </div>
              <div class="form-group" v-if="!showZoom">
                <label>
                  The default location will be your office set in your profile.
                  Check box to provide custom location.
                </label>
                <input
                  name="locationCheckbox"
                  id="locationCheckbox"
                  type="checkbox"
                  @change="showLocation = !showLocation"
                />
              </div>
              <div v-if="showLocation" class="form-group">
                <label> Provide location: </label>
                <Field
                  name="location"
                  id="location"
                  class="form-control"
                  type="text"
                />
              </div>
              <div class="form-group">
                <label>
                  Pick date and time (rounded to nearest 15 minute interval):
                </label>
                <Field
                  name="datepicker"
                  id="datepicker"
                  v-model="datetime"
                  :min="now"
                  type="datetime-local"
                />
                <br />
                <ErrorMessage name="datepicker" />
              </div>
              <div class="form-group">
                <button class="btn btn-primary">Create Appointment</button>
              </div>
            </Form>
            <div v-if="status">{{ status }}</div>
          </template>
        </iq-card>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import "tippy.js/dist/tippy.css"; // optional for styling

import { xray } from "../../../config/pluginInit";
import FullCalendar from "@fullcalendar/vue3";
import dayGridPlugin from "@fullcalendar/daygrid";
import { onMounted, reactive, ref, watch } from "vue";
import { doc, setDoc, Timestamp } from "@firebase/firestore";
import firebaseService from "../../../../firebase";
import iqCard from "../../../components/xray/cards/iq-card.vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as yup from "yup";
import getCollection from "../../../services/getCollection";
import getUsers from "../../../composables/getUsers";
import getDoctors from "../../../composables/getDoctor";
import tippy from "tippy.js";
import Popper from "vue3-popper";
import useCollection from "../../../services/useCollection";

export default {
  components: { iqCard, ErrorMessage },
  name: "GoogleCalendar",
  components: {
    FullCalendar,
    dayGridPlugin,
    Form,
    Field,
    ErrorMessage,
    Popper,
  },
  setup() {
    const config = ref({
      dateFormat: "Y-m-d",
      inline: true,
    });
    const now = new Date().toISOString().slice(0, -8);
    const showZoom = ref(false);
    const showLocation = ref(false);
    const status = ref(false);
    const datetime = ref(null);
    const showModal = ref(false);
    const selectedPatient = ref("default");
    const { users, error, loadUsers } = getUsers();
    const { doctor, loadDoctor } = getDoctors();
    const selectVerification = ref(null);
    let usersMap = reactive(new Map());
    const { documents } = getCollection("appointments");
    const {
      dbError: apptError,
      addDocToDb: addToAppointments,
      removeDocFromDb: removeAppointment,
      dbPending: apptPending,
    } = useCollection("appointments");
    const {
      dbError: emailsError,
      addDocToDb: addToEmails,
      dbPending: addEmailPending,
    } = useCollection("emails");
    const {
      dbError: remindersError,
      addDocToDb: addToReminders,
      removeDocFromDb: removeReminder,
      dbPending: addreminderPending,
    } = useCollection("reminders");
    const fullCalendar = ref(null);
    watch(fullCalendar, () => {
      setTimeout(() => fullCalendar.value.getApi().updateSize(), 1000);
    });
    let calendarOptions = reactive({
      plugins: [dayGridPlugin],
      initialView: "dayGridMonth",
      events: [],
      eventDidMount: function (info) {
        const props = info.event.extendedProps;
        if (props.doctorID === firebaseService.firebaseAuth.currentUser.uid) {
          if (usersMap.has(props.userID)) {
            const firstName = usersMap.get(props.userID)
              ? usersMap.get(props.userID).firstName
              : "name";

            tippy(info.el, {
              content: `<p> Location: ${props.location}</p> <p> Patient: ${
                // props.userID
                firstName
              } </p>`,
              allowHTML: true,
              placement: "top",
            });
          } else {
            watch(usersMap, () => {
              const firstName = usersMap.get(props.userID)
                ? usersMap.get(props.userID).firstName
                : "name";

              tippy(info.el, {
                content: `<p> Location: ${props.location}</p> <p> Patient: ${
                  // props.userID
                  firstName
                } </p>`,
                allowHTML: true,
                placement: "top",
              });
            });
          }
        }
      },
      eventClick: async function (clickInfo) {
        if (
          clickInfo.event.extendedProps.doctorID !==
          firebaseService.firebaseAuth.currentUser.uid
        )
          return;
        if (
          confirm(
            `Are you sure you want to delete the event '${clickInfo.event.title}`
          )
        ) {
          await removeAppointment(clickInfo.event.id);
          // console.log(clickInfo.event.id);
          await removeReminder(clickInfo.event.id);
          // console.log("reminder removed");
          await addToEmails({
            to: usersMap.get(clickInfo.event.extendedProps.userID).email,
            message: {
              subject: `Appointment cancelled with Doctor ${doctor.value.lastName}`,
              text: `Your appointment with Doctor ${
                doctor.value.lastName
              } on ${clickInfo.event.extendedProps.datetime.toDate()} was cancelled`,
            },
          });
          if (apptError.value || emailsError.value || remindersError.value) {
            alert("Error deleting appointment");
          }
        }
      },
    });
    const schema = yup.object({
      datepicker: yup.date().required("Date and time is required").nullable(),
    });
    const curUser = firebaseService.firebaseAuth.currentUser;

    onMounted(async () => {
      xray.index();
      await loadDoctor();
      await loadUsers();
      const patients = doctor.value.patients;
      const patientSet = new Set(patients);
      users.value = users.value.filter((user) => patientSet.has(user.id));
      for (let user of users.value) {
        usersMap.set(user.id, user);
      }
    });

    watch(documents, () => {
      selectedPatientChanged();
    });

    function selectedPatientChanged() {
      if (selectedPatient !== "default") {
        selectVerification.value = null;
      }
      calendarOptions.events = [];
      documents.value.forEach((doc) => {
        const docObj = { ...doc };
        const datetime = docObj.datetime;
        docObj.start = datetime.toDate();

        docObj.title = "Appt";
        if (docObj.doctorID === curUser.uid) {
          calendarOptions.events.push(docObj);
        }
        if (docObj.userID === selectedPatient.value) {
          if (docObj.doctorID === curUser.uid) {
            docObj.color = "#88FF88";
          } else {
            //Assigned by a different doctor, but still need to show it so patient does not get a conflicting appointment
            docObj.color = "#000000";
            calendarOptions.events.push(docObj);
          }
        }
      });
    }

    const bookAppointment = async (submittedObject) => {
      if (selectedPatient.value === "default") {
        selectVerification.value = "Please select a patient.";
        return;
      }
      status.value = null;

      const dateObj = new Date(submittedObject.datepicker);
      let ms = 1000 * 60 * 15; // convert minutes to ms
      let roundedDate = new Date(Math.round(dateObj.getTime() / ms) * ms);
      const date = Timestamp.fromDate(roundedDate);
      // const ref = collection(firebaseService.db, "appointments");
      let loc = "";
      if (showZoom.value) {
        loc = submittedObject.zoomLink;
      } else if (showLocation.value) {
        loc = submittedObject.location;
      } else if (!doctor.value.address) {
        status.value = "You don't have an address in your profile";
        return;
      } else {
        loc = doctor.value.address;
      }
      const res = await addToAppointments({
        isOnline: showZoom.value,
        doctorID: curUser.uid,
        datetime: date,
        location: loc,
        userID: selectedPatient.value,
      });
      //have to set doc with res.id
      await setDoc(doc(firebaseService.db, "reminders", res.id), {
        appointmentID: res.id,
        doctorID: curUser.uid,
        id: res.id,
        isFavourite: false,
        isRead: false,
        userID: selectedPatient.value,
      });
      await addToEmails({
        to: usersMap.get(selectedPatient.value).email,
        message: {
          subject: `Appointment made with Doctor ${doctor.value.lastName}`,
          text: `You have an appointment with Doctor ${doctor.value.lastName} on ${dateObj}`,
        },
      });
      if (apptError.value || emailsError.value || remindersError.value) {
        status.value = "Error: Check the fields";
      } else {
        status.value = "Success";
      }
    };

    return {
      config,
      showZoom,
      showLocation,
      bookAppointment,
      status,
      datetime,
      calendarOptions,
      schema,
      showModal,
      selectedPatient,
      selectedPatientChanged,
      now,
      users,
      fullCalendar,
      selectVerification,
    };
  },
};
</script>

<style scoped>
.popper-content {
  width: fit-content;
  height: max-content;
  padding: 0.5rem 0.75rem;
  color: #212529;
  font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
    Helvetica Neue, Arial, Noto Sans, sans-serif, Apple Color Emoji,
    Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji;
  font-weight: 400;
  line-height: 1.5;
  font-size: 0.875rem;
  background-color: var(--light-grey);
  background-clip: padding-box;
  border: 1px solid rgba(0, 0, 0, 0.2);
  border-radius: 0.3rem;
}
</style>
